import { motion } from "framer-motion";
import { ArrowRight, Globe, Handshake, Rocket, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { whyChooseTranslations } from "@/utils/translations/whyChoose";
import { localizePath } from "@/utils/languageRoutes";
import { EASE } from "./demo-shared";

interface DemoWhyChooseProps {
  language: string;
}

const icons = [Rocket, Handshake, Search, Globe];
const tints = [
  "bg-sky-50 text-primary",
  "bg-amber-50 text-secondary-dark",
  "bg-sky-50 text-primary",
  "bg-amber-50 text-secondary-dark",
];

const DemoWhyChoose = ({ language }: DemoWhyChooseProps) => {
  const isKo = language === "KO";
  const t = whyChooseTranslations[language as keyof typeof whyChooseTranslations] || whyChooseTranslations.EN;

  return (
    <section className="relative bg-[#FAFAF8] py-24 md:py-32">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          {/* Sticky intro */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <motion.div
              initial={{ opacity: 0, y: 28, filter: "blur(6px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [...EASE] }}
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-semibold text-secondary">02</span>
                <span aria-hidden className="h-px w-8 bg-slate-300" />
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {isKo ? "두루의 강점" : "Why Duru"}
                </span>
              </div>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">{t.title}</h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-slate-600">{t.subtitle}</p>
              <Link to={localizePath("/contact", language)} className="mt-8 inline-block">
                <Button
                  size="lg"
                  className="group rounded-xl bg-primary px-6 py-5 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-dark"
                >
                  {isKo ? "전문가와 상담하기" : "Talk to Our Experts"}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Numbered feature rows */}
          <div>
            {t.differentiators.map((item, index) => {
              const Icon = icons[index % icons.length];
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 32 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: index * 0.06, ease: [...EASE] }}
                  className="group grid grid-cols-[auto_auto_1fr] items-start gap-5 border-t border-slate-200 py-8 transition-colors first:border-t-0 hover:bg-white md:gap-7 md:px-4 md:-mx-4 md:rounded-2xl"
                >
                  <span className="pt-1 font-mono text-sm font-semibold text-slate-300 transition-colors group-hover:text-secondary">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${tints[index % tints.length]} transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110`}
                  >
                    <Icon size={20} strokeWidth={2} />
                  </span>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoWhyChoose;
